import { useState, useEffect } from 'react';
import { Storage } from '../storage';
import { formatDate, hasTextContent, stripHtml, daysBetween } from '../utils';
import { DEFAULT_COLORS } from '../constants';
import TimelineCanvas from '../components/TimelineCanvas';
import Modal from '../components/Modal';

// ─── TIMELINE EMBED PAGE ──────────────────────────────────────────────────────
//
//  Minimal read-only page rendered at /embed/:timelineId.
//  No nav, no toolbar export buttons, no admin controls, no social features.
//  Layout toggle is hidden — embed always renders whatever layout the owner set.
//  The timeline's color theme (and dark/light mode) are applied from the stored
//  timeline settings rather than the viewer's system preference.
//
//  Props:
//    timelineId {string}  the timeline ID parsed from the URL
//

const toYtEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u    = new URL(url.trim());
    const host = u.hostname.replace('www.', '').replace('m.', '');
    let id = null;
    if (host === 'youtu.be') { id = u.pathname.slice(1).split('/')[0]; }
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) { id = u.pathname.split('/')[2]; }
      else { id = u.searchParams.get('v'); }
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}`;
  } catch {}
  return null;
};

const YouTubeEmbed = ({ embedUrl, title }) => {
  const [hasError, setHasError] = useState(false);
  const src = toYtEmbedUrl(embedUrl);
  if (!src || hasError) return null;
  return (
    <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden', marginBottom: 16, background: 'var(--bg2)' }}>
      <iframe
        src={src}
        title={title || 'Event video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onError={() => setHasError(true)}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
      />
    </div>
  );
};

const TimelineEmbed = ({ timelineId }) => {
  const [timeline,       setTimeline]       = useState(null);
  const [error,          setError]          = useState('');
  const [loading,        setLoading]        = useState(true);
  const [selectedEvent,  setSelectedEvent]  = useState(null);

  useEffect(() => {
    Storage.getTimelineForEmbed(timelineId)
      .then(({ timeline: tl }) => { setTimeline(tl); setLoading(false); })
      .catch(err => { setError(err.message || 'Could not load timeline.'); setLoading(false); });
  }, [timelineId]);

  // Apply the timeline's saved theme so the embed renders in the owner's chosen
  // appearance regardless of the viewer's system or browser preference.
  useEffect(() => {
    if (!timeline) return;
    const theme = timeline.theme || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
    return () => {
      // Restore system preference when embed unmounts (edge case — usually the
      // embed page lives in its own iframe so this is cosmetic.)
      document.documentElement.removeAttribute('data-theme');
    };
  }, [timeline]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--bg)',
      }}>
        <p style={{ color: 'var(--text3)', fontSize: 14, fontFamily: 'var(--font-body)' }}>
          Loading…
        </p>
      </div>
    );
  }

  const isPrivate = error.toLowerCase().includes('private');

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: 'var(--bg)',
        padding: 32, fontFamily: 'var(--font-body)',
      }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 52, marginBottom: 18 }} aria-hidden="true">
            {isPrivate ? '🔒' : '⚠️'}
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 22,
            color: 'var(--text)', marginBottom: 10,
          }}>
            {isPrivate ? 'This timeline is private' : 'Timeline not found'}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
            {isPrivate
              ? 'The author has not made this timeline available for public embedding.'
              : 'This timeline does not exist or has been removed.'}
          </p>
        </div>
      </div>
    );
  }

  const colors = timeline.colors || DEFAULT_COLORS;
  const sorted = [...timeline.events].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));

  return (
    <div style={{
      background: 'var(--bg)',
      minHeight: '100vh',
      padding: 16,
      boxSizing: 'border-box',
      fontFamily: 'var(--font-body)',
    }}>
      {/* ── Timeline header ── */}
      <div style={{ marginBottom: 16, padding: '0 4px' }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          fontWeight: 700,
          color: colors.primary,
          margin: 0,
          lineHeight: 1.25,
        }}>
          {timeline.title}
        </h1>
        {hasTextContent(timeline.description) && (
          <p style={{
            fontSize: 14,
            color: colors.secondary,
            marginTop: 6,
            marginBottom: 0,
            lineHeight: 1.6,
          }}>
            {stripHtml(timeline.description)}
          </p>
        )}
      </div>

      <TimelineCanvas timeline={timeline} onEventClick={setSelectedEvent} isEmbed />

      {sorted.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 16,
            color: colors.primary, marginBottom: 14,
          }}>
            All Events ({sorted.length})
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 12,
          }}>
            {sorted.map(ev => (
              <div
                key={ev.id}
                style={{
                  background: `${colors.tertiary}cc`,
                  border: `1px solid ${colors.primary}30`,
                  borderRadius: 'var(--radius-lg)',
                  padding: '14px 18px',
                }}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.primary, marginBottom: 5 }}>
                  {ev.title}
                </h3>
                <p style={{ fontSize: 11, color: colors.secondary, marginBottom: hasTextContent(ev.description) ? 8 : 0 }}>
                  {formatDate(ev.startDate, ev.startEra !== 'NONE')}{ev.endDate && ev.endDate !== ev.startDate ? ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : ''}
                </p>
                {hasTextContent(ev.description) && (
                  <p style={{
                    fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  }}>
                    {stripHtml(ev.description)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      {/* ── Event detail modal ── */}
      <Modal
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title || ''}
        width={520}
      >
        {selectedEvent && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                {formatDate(selectedEvent.startDate, selectedEvent.startEra !== 'NONE')}
              </span>
              {selectedEvent.endDate && selectedEvent.endDate !== selectedEvent.startDate && (<>
                <span aria-hidden="true" style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>→</span>
                <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--accent)' }}>
                  {formatDate(selectedEvent.endDate, selectedEvent.endEra !== 'NONE')}
                </span>
                <span style={{ fontSize: 12, background: 'var(--bg3)', borderRadius: 6, padding: '4px 10px', color: 'var(--text3)' }}>
                  {daysBetween(selectedEvent.startDate, selectedEvent.endDate)} days
                </span>
              </>)}
            </div>

            {selectedEvent.photo && (
              <img
                src={selectedEvent.photo}
                alt={`Photo for ${selectedEvent.title}`}
                style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 'var(--radius)', marginBottom: 16, border: '1px solid var(--border)' }}
              />
            )}

            {selectedEvent.videoUrl && (
              <YouTubeEmbed embedUrl={selectedEvent.videoUrl} title={selectedEvent.title} />
            )}

            {hasTextContent(selectedEvent.description) ? (
              <div
                dangerouslySetInnerHTML={{ __html: selectedEvent.description }}
                style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}
              />
            ) : (
              <p style={{ color: 'var(--text3)', fontSize: 14, lineHeight: 1.7, marginBottom: selectedEvent.sources?.length ? 20 : 0 }}>
                No description provided.
              </p>
            )}

            {selectedEvent.sources?.length > 0 && (
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  Sources ({selectedEvent.sources.length})
                </p>
                {selectedEvent.sources.map((src, i) => (
                  <div
                    key={src.id || i}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8 }}
                  >
                    {src.label && <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: src.text ? 6 : 0 }}>{src.label}</p>}
                    {src.text  && <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{src.text}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TimelineEmbed;
